import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { AISpendMeter } from '@/components/AISpendMeter';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface BudgetExhaustedModalProps {
  open: boolean;
  /** Closes the modal — shared by "OK", the X, Esc, and overlay click. */
  onClose: () => void;
  /** From AIBudgetStatus.current_usage. */
  currentUsage?: number;
  /** From AIBudgetStatus.max_limit. */
  maxLimit?: number;
  /** From AIBudgetStatus.reset_duration. */
  resetDuration?: string;
  /** From AIBudgetStatus.calendar_aligned. */
  calendarAligned?: boolean;
  /**
   * True while the 402-path budget refetch is in flight. When set, the meter
   * slot renders a skeleton placeholder instead of `AISpendMeter` so the modal
   * never flashes stale figures with a contradictory "Limit reached" caption.
   * The pre-flight 100% gate path never sets this (its cached figures are
   * already the at-rejection truth), so it shows figures immediately.
   */
  refreshing?: boolean;
}

/**
 * Informational-only modal shown when the tenant's monthly AI budget is fully
 * used so Hannibal cannot launch this cycle. Triggered at two points (the
 * pre-flight 100% gate and the backend's HTTP 402 on final launch) — same
 * modal, same content.
 *
 * The customer cannot self-serve raise the limit (only Praetorian sets it), so
 * there is no "add funds" / support CTA — the single control is "OK" plus
 * the Dialog's built-in close X and Esc/overlay dismiss.
 *
 * Reuses the shared `AISpendMeter` so the spend visualization is byte-identical
 * to the Org-settings AI Spend card.
 */
export const BudgetExhaustedModal: React.FC<BudgetExhaustedModalProps> = ({
  open,
  onClose,
  currentUsage,
  maxLimit,
  resetDuration,
  calendarAligned,
  refreshing = false,
}) => {
  // Figures are available only when both numbers are present and the cap is
  // positive (a non-positive cap is misconfigured — omit the meter, never
  // divide by zero). Mirrors design-spec §8 missing-data states.
  const hasFigures =
    typeof currentUsage === 'number' &&
    typeof maxLimit === 'number' &&
    maxLimit > 0;

  // Reset sentence: only stated when we can render it truthfully (never
  // fabricate a date). design-spec §5 / §8.
  let resetSentence = '';
  if (calendarAligned === true) {
    resetSentence =
      ' The budget resets at the start of your next monthly cycle.';
  } else if (calendarAligned === false && resetDuration) {
    resetSentence = ` The budget resets every ${resetDuration}.`;
  }

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-w-md" data-testid="budget-exhausted-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExclamationTriangleIcon
              className="text-chariot-text-red size-5"
              aria-hidden="true"
            />
            Remaining AI Budget Insufficient
          </DialogTitle>
          <DialogDescription>
            Hannibal can&apos;t launch right now.
          </DialogDescription>
        </DialogHeader>

        {refreshing ? (
          // 402 path: the background refetch is in flight. Skeleton the meter
          // slot rather than render AISpendMeter with the old cached figures —
          // otherwise the modal briefly shows stale numbers with a contradictory
          // "Limit reached" caption until the refresh resolves. The modal still
          // opens instantly; only the figures wait for fresh data.
          <div
            className="bg-chariot-background-secondary rounded-md p-4"
            data-testid="budget-meter-skeleton"
            aria-hidden="true"
          >
            <div className="flex w-full flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="bg-chariot-background-primary h-2 w-full max-w-xs animate-pulse rounded-full" />
                <div className="bg-chariot-background-primary h-3 w-20 animate-pulse rounded" />
              </div>
              <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
                <div className="bg-chariot-background-primary h-5 w-16 animate-pulse rounded" />
                <div className="bg-chariot-background-primary h-3 w-24 animate-pulse rounded" />
              </div>
            </div>
          </div>
        ) : (
          hasFigures && (
            <div className="bg-chariot-background-secondary rounded-md p-4">
              <AISpendMeter
                currentUsage={currentUsage}
                maxLimit={maxLimit}
                hasLimit
                atLimit
              />
            </div>
          )
        )}

        <p className="text-chariot-text-secondary text-sm leading-relaxed">
          Your remaining AI budget is below the threshold required to start a
          new hunt this cycle.{resetSentence}
        </p>

        <span className="text-chariot-text-secondary text-xs">
          Managed by Praetorian · Set by your Guard Intelligence tier.
        </span>

        <DialogFooter>
          <Button className="h-11 px-6" onClick={onClose}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
